import React from 'react';
import { Form } from 'antd';
import { getCredPayoutAccount, getPayerRequirements } from 'api/credentialing';
import { MedicaidGroupFields, entityHasAgreement } from 'components/CredentialingPayerSteps';
import { npiIssue, type TNpiIssue } from 'utils/npiCheck';
import { ENTITY_TYPE_OTHER } from '../../constants';
import { PracticeEnrollment, TCredentialingProvider, OrgCredentialingPayerRef } from 'types/credentialing';
import PracticePayersCard from '../PracticePayersCard';
import PracticeEnrollmentFields from '../PracticeEnrollmentFields';
import s from './s.module.less';

interface PracticeSetupStepProps {
    enrollment: PracticeEnrollment;
    onEnrollmentChange: (next: PracticeEnrollment) => void;
    roster: TCredentialingProvider[];
    initialPayers?: OrgCredentialingPayerRef[];
    // Group NPI 的 NPI Registry 校验结果上报给父级，用来拦 Next。NpiRegistryBox 用 setFieldValue
    // 写查询态，这条路径不经过 onValuesChange，所以父级镜像的 enrollment 里拿不到最新状态，
    // 必须单独 watch 出来上报，否则查询完成后父级永远停在「查询中」
    onNpiIssueChange?: (issue: TNpiIssue) => void;
}

// Step 1 of 2 (group onboarding): register the practice as a billing entity (layer 1) and choose
// the payers the whole practice is credentialing for. Providers are added in Step 2.
const PracticeSetupStep: React.FC<PracticeSetupStepProps> = ({
    enrollment,
    onEnrollmentChange,
    roster,
    initialPayers = [],
    onNpiIssueChange,
}) => {
    const [form] = Form.useForm<PracticeEnrollment>();

    // NpiRegistryBox 写进表单的查询态 → 判定 → 上报父级（父级据此拦 Next）
    const groupNpi = Form.useWatch('groupNpi', form);
    const npiType = Form.useWatch('npiType', form);
    const npiChecking = Form.useWatch('npiChecking', form);
    const npiState = npiIssue({ groupNpi, npiType, npiChecking }, { npiKey: 'groupNpi', expectType: 'Type 2' });
    React.useEffect(() => {
        onNpiIssueChange?.(npiState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [npiState]);

    // antd only applies `initialValues` at Form mount, but the enrollment arrives async (or the
    // parent state is filled by an effect after mount on deep-link/refresh entries), so external
    // changes must be pushed into the form explicitly. The ref skips the echo of our own
    // onValuesChange mirror — without it every keystroke would round-trip through
    // setFieldsValue, which can break IME composition.
    const selfChangeRef = React.useRef(false);
    // 最新 enrollment 的引用：onValuesChange 回传的 all 只含表单上注册过的字段，要叠在完整 enrollment 上，
    // 否则 practiceRequirements / w9FileUrl 等不在表单里的字段第一次输入就被冲掉（接口整份覆盖）
    const enrollmentRef = React.useRef(enrollment);
    enrollmentRef.current = enrollment;
    React.useEffect(() => {
        if (selfChangeRef.current) {
            selfChangeRef.current = false;
            return;
        }
        form.setFieldsValue(enrollment);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enrollment]);

    // Mirror the antd form state back up to the parent. Switching entity type away from "Other"
    // clears the free-text field so a stale value never leaks into the payload.
    const handleValuesChange = (changed: Partial<PracticeEnrollment>, all: PracticeEnrollment) => {
        selfChangeRef.current = true;
        if ('entityType' in changed && changed.entityType !== ENTITY_TYPE_OTHER) {
            form.setFieldValue('entityTypeOther', '');
            onEnrollmentChange({ ...enrollmentRef.current, ...all, entityTypeOther: '' });
            return;
        }
        onEnrollmentChange({ ...enrollmentRef.current, ...all });
    };

    // 原型 mcdGroupInner（09/21）：practice payers 里有 Medicaid 时，按州问 group 是否已注册，
    // 任一州 No 打开 practice 级注册问卷。不拦 Next（漏答的在 Review 上兜住）
    const [practicePayers, setPracticePayers] = React.useState<OrgCredentialingPayerRef[]>(initialPayers);
    const medicaidStates = React.useMemo(
        () => Array.from(new Set(practicePayers.filter((p) => /medicaid/i.test(p.payerName)).map((p) => p.state))),
        [practicePayers],
    );
    // 预填依据（group 版只用到 16b 与 25）：owner 本人任一州 Medicaid 已注册、已有 payout 账户
    const [ownerEverEnrolled, setOwnerEverEnrolled] = React.useState(false);
    const [payoutOnFile, setPayoutOnFile] = React.useState(false);
    React.useEffect(() => {
        if (!medicaidStates.length) return;
        getPayerRequirements()
            .then((res: any) =>
                setOwnerEverEnrolled((res?.data?.data?.medicaid || []).some((m: any) => m?.enrolled === true)),
            )
            .catch(() => undefined);
        getCredPayoutAccount()
            .then((res: any) => setPayoutOnFile(!!res?.data?.data?.accountNumber))
            .catch(() => undefined);
    }, [medicaidStates.length]);

    return (
        <div className={s.wrap}>
            <div className={s.title}>Set up your practice</div>
            <div className={s.subtitle}>
                Register your practice as a billing entity and choose the payers you&apos;re credentialing for. Step 1
                of 2.
            </div>

            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                className={s.form}
                initialValues={enrollment}
                onValuesChange={handleValuesChange}
            >
                {/* Practice enrollment (group layer 1) */}
                <div className={s.card}>
                    <div className={s.cardTitle}>Practice enrollment</div>
                    <div className={s.cardDesc}>
                        The group-level details that register your practice as a billing entity with payers. Collected
                        once for the whole practice.
                    </div>

                    <PracticeEnrollmentFields form={form} npiBoxClassName={s.npiBox} />
                </div>
            </Form>

            <PracticePayersCard roster={roster} initialPayers={initialPayers} onPayersChange={setPracticePayers} />

            {medicaidStates.length > 0 && (
                <div className={s.card}>
                    <div className={s.cardTitle}>Medicaid group enrollment</div>
                    <div className={s.cardDesc}>
                        We enroll your practice in Medicaid as a group. This needs 2 or more providers on your roster; a
                        practice with one provider enrolls as an individual instead.
                    </div>
                    <MedicaidGroupFields
                        states={medicaidStates}
                        value={enrollment.practiceRequirements}
                        onChange={(practiceRequirements) => onEnrollmentChange({ ...enrollment, practiceRequirements })}
                        showOrgAgreement={entityHasAgreement(enrollment.entityType)}
                        derive={{
                            everEnrolled:
                                ownerEverEnrolled ||
                                (enrollment.practiceRequirements?.medicaid || []).some((m) => m.enrolled === true),
                            payoutOnFile,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default PracticeSetupStep;
